import { Alert, Box, Typography } from "@mui/material";
import { type SxProps, type Theme } from "@mui/material/styles";
import { useCallback } from "react";

import { type StartConfigMigrationInput } from "../../generated/graphql";
import ConfigMigrationForm from "./config-migration-form";
import ConfigMigrationReport from "./config-migration-report";
import { useConfigMigration } from "./use-config-migration";

const styles = {
  subtitle: { mt: 0.5, mb: 3 },
  error: { mb: 2, maxWidth: 560 },
} satisfies Record<string, SxProps<Theme>>;

/**
 * Config migration tool container. Wires the form to the orchestration hook and
 * renders the live status/report. Holds no presentation logic itself: state and
 * polling live in `useConfigMigration`; rendering lives in the form and report
 * components.
 */
const ConfigMigration = () => {
  const { start, starting, startError, status } = useConfigMigration();

  const handleSubmit = useCallback(
    (input: StartConfigMigrationInput) => {
      void start(input);
    },
    [start],
  );

  return (
    <Box>
      <Typography variant="h5">Config migration</Typography>
      <Typography color="text.secondary" variant="body2" sx={styles.subtitle}>
        Copy a company&apos;s configuration between environments. Start with a
        dry run to preview the changes; nothing is written until you turn the
        dry run off.
      </Typography>

      {startError === undefined ? null : (
        <Alert severity="error" sx={styles.error}>
          {startError.message}
        </Alert>
      )}

      <ConfigMigrationForm submitting={starting} onSubmit={handleSubmit} />
      <ConfigMigrationReport status={status} />
    </Box>
  );
};

export default ConfigMigration;
